import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { makeTableColumns } from '@core/models/entities/entity-visualization-interface';
import { EntityLabelsTranslationService } from '@core/services/entity-labels-translation/entity-labels-translation.service';
import { UserModelsRegistryService } from '@core/services/user-models-registry/user-models-registry.service';
import { selectAvailableEntities } from '@core/store/dataspace/dataspace.selectors';

/** A selectable extra-info field for a referenced entity type's popover field-picker. */
export interface ReferenceFieldOption {
  key: string;
  label: string;
}

/**
 * Resolves the extra-info fields available for a referenced entity type's
 * popover, reusing the same schema → columns → labels pipeline the entity
 * table itself uses. No new schema plumbing.
 */
@Injectable({ providedIn: 'root' })
export class ReferenceFieldsService {
  private readonly userModelsRegistry = inject(UserModelsRegistryService);
  private readonly entityLabelsTranslation = inject(EntityLabelsTranslationService);
  private readonly store = inject(Store);

  private readonly availableEntities = this.store.selectSignal(selectAvailableEntities);

  /** Resolves the current dataspace's schema versionName for an entity type, or undefined if unknown. */
  resolveVersionName(entityType: string): string | undefined {
    for (const entity of this.availableEntities()) {
      // Case-insensitive match: the schema's uidEntityType (e.g. 'SITE') arrives
      // uppercased, while entities.service force-lowercases entity.versions[].entityType
      // (e.g. 'site') at the store source. Don't switch this back to a plain `===`.
      const version = entity.versions.find(
        (v) => v.entityType.toUpperCase() === entityType.toUpperCase()
      );
      if (version) return version.versionName;
    }
    return undefined;
  }

  /** Available `{key,label}` fields for the entity type's popover field-picker, or [] if not resolvable yet. */
  availableFields(entityType: string): ReferenceFieldOption[] {
    const versionName = this.resolveVersionName(entityType);
    if (!versionName) return [];

    const entityCfg = this.userModelsRegistry.getEntityCfg(versionName);
    if (!entityCfg) return [];

    const columns = makeTableColumns(entityCfg);
    return this.entityLabelsTranslation
      .translateTableColumns(columns)
      .map((col) => ({ key: String(col.key), label: col.label }));
  }
}
